/*
###################################################################
Fitinsur Open Insurance - Copyright 2019-2023 - All Rights Reserved
###################################################################
*/

import React, { useState, useEffect, useContext, useRef, useCallback  } from 'react';
import Icon from 'components/Icon';
import { FieldArray, Field, Form, Formik } from 'formik';
import * as yup from 'yup';
import {
  Table,
  TableHead,
  TableRow,
  TableHeader,
  TableBody,
  TableCollum,
  ViewText,
  ButtonContainer,
  ButtonContent,
  LabelEdit,
  InlineLabel
} from './styles';
import { GridColumnTitle, Label } from 'components/Form';
import Row from 'components/Grid/Row';
import Col from 'components/Grid/Col';
import InputField from 'components/Form/InputField';
import SelectField from 'components/Form/SelectField';
import { FitinsurAPI, MessageCenter } from 'utils';
import Button from 'components/Button';
import { GlobalLoaderContext, GlobalAuthContext } from 'store/context';
import ModalConfirmDelete from 'components/ModalConfirmDelete';
import { debounce } from "lodash";
import SearchField from 'components/Form/SearchField';

const AccountInformation = ({ columns, data, products, hasPermission, idPerson, onSavePersonRelationship }) => {
  const formikRef = useRef(null);
  const inputRef = useRef(null);
  const fitinsur = new FitinsurAPI();
  const mc = new MessageCenter();
  const loading = useContext(GlobalLoaderContext);
  const authContext = useContext(GlobalAuthContext);
  const [validations, setValidations] = useState({});

  const [sortBy, setSortBy] = useState({ key: null, direction: null });
  const [addAccount, setAddAccount] = useState(false);
  const [indexAccount, setIndexAccount] = useState(null);
  const [relationshipContent, setRelationshipContent] = useState([]);
  const [searchList, setSearchList] = useState([])
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [productCurrencies, setProductsCurrencies] = useState([]);

  const [initial, setInitial] = useState({
    product: '',
    accountCurrency: '',
    accountName: '',
    bankSortCode: '',
    bankSwiftBIC: '',
    accountNumber: '',
    bankIBAN: '',
    correspbank: '',
    transitAbaNumber: '',
    routingNumber: '',
    correspbank_BIC: '',
    bankName: ''
  });



  const setEditAccount = (account, indexAccount) => {
    formikRef.current.values.product = '';
    const currency = { idCurrency: 'id', currencyName: account.accountCurrency };
    const optionCurrency = getFiledOptions(
      [{ 
        currency_id: currency.idCurrency, 
        currency_code: currency.currencyName 
      }], 
      'currency'
    );
    setProductsCurrencies(optionCurrency);
    formikRef.current.values.accountCurrency = currency;
    formikRef.current.values.accountName = account.accountName;
    formikRef.current.values.bankSortCode = account.bankSortCode;
    formikRef.current.values.bankSwiftBIC = account.bankSwiftBIC;
    formikRef.current.values.accountNumber = account.accountNumber;
    formikRef.current.values.bankIBAN = account.bankIBAN;
    formikRef.current.values.correspbank = account.correspbank;
    formikRef.current.values.transitAbaNumber = account.transitAbaNumber;
    formikRef.current.values.routingNumber = account.routingNumber;
    formikRef.current.values.correspbank_BIC = account.correspbank_BIC;
    formikRef.current.values.bankName = account.bankName || null

    setAddAccount(true);
    setIndexAccount(indexAccount);
    setTimeout(() => inputRef.current && inputRef.current.focus(), 100)
  }

  const resetForm = () => {
    formikRef.current.values.product = '';
    formikRef.current.values.accountCurrency = '';
    formikRef.current.values.accountName = '';
    formikRef.current.values.bankSortCode = '';
    formikRef.current.values.bankSwiftBIC = '';
    formikRef.current.values.accountNumber = '';
    formikRef.current.values.bankIBAN = '';
    formikRef.current.values.correspbank = '';
    formikRef.current.values.transitAbaNumber = '';
    formikRef.current.values.routingNumber = '';
    formikRef.current.values.correspbank_BIC = '';
    formikRef.current.values.bankName = '';
  }

  const saveAccount = async (values) => {
    try {
      loading.start();

      const account = {
        accountCurrency: values.accountCurrency.currencyName,
        accountName: values.accountName,
        accountNumber: values.accountNumber,
        bankIBAN: values.bankIBAN,
        bankSortCode: values.bankSortCode,
        bankSwiftBIC: values.bankSwiftBIC,
        correspbank: values.correspbank,
        transitAbaNumber: values.transitAbaNumber,
        routingNumber: values.routingNumber,
        correspbank_BIC: values.correspbank_BIC,
        bankName: values.bankName || null,
        idPersonTailBank: values.idPersonTailBank || null
      }

      let finalArrayrelationshipContent = relationshipContent;

      //When indexAccount exists, the account is being edited, otherwise a new account is being added
      if(indexAccount !== null && indexAccount !== undefined) {
        finalArrayrelationshipContent.splice(indexAccount, 1, account);
      } else {
        finalArrayrelationshipContent = [...relationshipContent, account];
      }

      const body = {
        idPersonHead: idPerson,
        idPersonTail: !data.length ? values.idPersonTailBank : data[0].idPersonTail,
        idPersonRelationship: !data.length ? null : data[0].idPersonRelationship,
        idDomain: 380,
        relationshipContent: finalArrayrelationshipContent,
      }

      fitinsur.setAuthToken(authContext.token);
      const response = await fitinsur.requestSavePersonRelationship(body).then(res => res.json());
      if (response.ret_code !== 200) {
        mc.error(response.ret_msg_details)
        loading.stop();
        return;
      }

      setIndexAccount(null);
      setAddAccount(false);
      resetForm();
      onSavePersonRelationship && onSavePersonRelationship();
    } catch (err) {
      loading.stop();
      mc.error("An error happened, please try again.");
    };
  }

  const deleteAccount = async (indexAccount) => {
    try {
      loading.start();
      
      const relationshipContentFiltered = relationshipContent.filter(
        (item, index) => index !== indexAccount
      );

      const body = {
        idPersonHead: idPerson,
        idPersonTail: data[0].idPersonTail,
        idPersonRelationship: data[0].idPersonRelationship,
        idDomain: 380,
        relationshipContent: relationshipContentFiltered,
      }

      fitinsur.setAuthToken(authContext.token);
      const response = await fitinsur.requestSavePersonRelationship(body).then(res => res.json());
      setIndexAccount(null);
      if (response.ret_code !== 200) {
        mc.error(response.ret_msg_details)
        loading.stop();
        return;
      }

      onSavePersonRelationship && onSavePersonRelationship();
    } catch (err) {
      setIndexAccount(null);
      loading.stop();
      mc.error("An error happened, please try again.");
    };
  }

  const createValidation = () => {
    let validations = yup.object().shape({
      bankName: data.length ? yup.string().notRequired().nullable() : yup.string().required("* Required field"),
      accountCurrency: yup.string().required("* Required field"),
      accountName: yup.string().required("* Required field"),
      bankSortCode: yup.string().required("* Required field"),
      bankSwiftBIC: yup.string().required("* Required field"),
      accountNumber: yup.string().required("* Required field"),
      bankIBAN: yup.string().required("* Required field"),
      correspbank: yup.string().required("* Required field"),
      transitAbaNumber: yup.string(),
      routingNumber: yup.string(),
      correspbank_BIC: yup.string().required("* Required field"),
    });
    setValidations(validations);
    return validations
  }

  useEffect(() => {
    createValidation();
  }, []);

  useEffect(() => {
    if(data.length)
      setRelationshipContent(data[0].relationshipContent)
  }, [data]);

  const searchBanks = async (searchValue) => {
    try {
      fitinsur.setAuthToken(authContext.token);
      fitinsur.requestGetPersonsList(`?fullName=${searchValue}&personType=35`).then((response) => response.json()).then((data) => {
        if (data.ret_code != 200) {
          return;
        }

        data.ret_value = data.ret_value?.map(v => ({ ...v, id: v.idPerson, personName: `${v.lastName}` }))
        setSearchList(data.ret_value);
      });
    } catch (err) {
      console.log("FETCH_FAILURE: ", err);
    }
  }

  const handleProductCurrencies = function (product) {
    let formattedCurrencies = [];
    if(product && product.idProduct) {
      const { metadata } = products.find(p => p.idProduct === product.idProduct);

      if(metadata.currencies && metadata.currencies.length) {
        formattedCurrencies = getFiledOptions(metadata.currencies, 'currency');

        setProductsCurrencies(formattedCurrencies);
      }
    }

    if(!formattedCurrencies.length)
      setProductsCurrencies(formattedCurrencies);
  }

  const getFiledOptions = function (answerOptions, optionType) {
    return answerOptions.map(option => {
      if (optionType === 'product') {
        return {
          label: option.productName,
          value: {
            idProduct: option.idProduct,
            productName: option.productName
          }
        };
      } else if (optionType === 'currency') {
        return {
          label: option.currency_code,
          value: {
            idCurrency: option.currency_id,
            currencyName: option.currency_code,
          }
        }
      }
    });
  }

  const debouncedChangeHandler = useCallback(
    debounce(searchBanks, 300)
    , []);

  return (
    <Formik
      innerRef={formikRef}
      initialValues={initial}
      enableReinitialize={true}
      onSubmit={(values) => saveAccount(values)}
      validationSchema={validations}
    >
      {({ setFieldValue, values }) => (
        <>
          <ModalConfirmDelete show={deleteModalOpen} onModalClose={() => { setDeleteModalOpen(false); setIndexAccount(null); }} onConfirm={() => deleteAccount(indexAccount)} />
          <Table>
            <TableHead>
              <TableRow>
                {columns.map((column, index) =>
                  <TableHeader right={column.right} key={index} minWidth={column.minWidth} maxWidth={column.maxWidth}>
                    <GridColumnTitle style={{ cursor: column.value ? 'pointer' : 'not-allowed' }}>
                      {column.label}
                    </GridColumnTitle>
                    {column.value && sortBy.key === column.value && sortBy.direction === "asc" ?
                      <Icon name={"arrowup"} /> :
                      column.value && sortBy.key === column.value && sortBy.direction === "desc" ?
                        <Icon name={"arrowdown"} /> :
                        ''}
                  </TableHeader>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {relationshipContent.length ? <FieldArray
                name="management"
                render={() =>
                  relationshipContent.map((item, index) => (
                    <>
                      <TableRow key={index} >
                        <TableCollum>
                          <InlineLabel>
                            Bank
                          </InlineLabel>
                          <LabelEdit fontSize={12}>
                            {item.bankName || (data && data[0].personTailLastName)} 
                          </LabelEdit>
                        </TableCollum>

                        <TableCollum>
                          <InlineLabel>
                            Currency
                          </InlineLabel>
                          <LabelEdit fontSize={12}>
                            {item.accountCurrency}
                          </LabelEdit>
                        </TableCollum>

                        <TableCollum>
                          <InlineLabel>
                            Name
                          </InlineLabel>
                          <LabelEdit fontSize={12}>
                            {item.accountName}
                          </LabelEdit>
                        </TableCollum>

                        <TableCollum>
                          <InlineLabel>
                            Sort Code
                          </InlineLabel>
                          <LabelEdit fontSize={12}>
                            {item.bankSortCode}
                          </LabelEdit>
                        </TableCollum>

                        <TableCollum>
                          <InlineLabel>
                            Swift
                          </InlineLabel>
                          <LabelEdit fontSize={12}>
                            {item.bankSwiftBIC}
                          </LabelEdit>
                        </TableCollum>

                        <TableCollum>
                          <InlineLabel>
                            Acc. Number
                          </InlineLabel>
                          <LabelEdit fontSize={12}>
                            {item.accountNumber}
                          </LabelEdit>
                        </TableCollum>

                        <TableCollum>
                          <InlineLabel>
                            IBAN
                          </InlineLabel>
                          <LabelEdit fontSize={12}>
                            {item.bankIBAN}
                          </LabelEdit>
                        </TableCollum>

                        <TableCollum>
                          <InlineLabel>
                            Corresp. Bank
                          </InlineLabel>
                          <LabelEdit fontSize={12}>
                            {item.correspbank}
                          </LabelEdit>
                        </TableCollum>

                        <TableCollum>
                          <InlineLabel>
                          ACH Routing Number
                          </InlineLabel>
                          <LabelEdit fontSize={12}>
                            {item.routingNumber}
                          </LabelEdit>
                        </TableCollum>

                        <TableCollum>
                          <InlineLabel>
                          Transit/ABA Number (NY)
                          </InlineLabel>
                          <LabelEdit fontSize={12}>
                            {item.transitAbaNumber}
                          </LabelEdit>
                        </TableCollum>

                        <TableCollum>
                          <InlineLabel>
                            Corresp. BIC
                          </InlineLabel>
                          <LabelEdit fontSize={12}>
                            {item.correspbank_BIC}
                          </LabelEdit>
                        </TableCollum>

                        <TableCollum right>
                          <InlineLabel>
                            ACTION
                          </InlineLabel>
                          <Label>
                            <Icon
                              name="edit"
                              style={{ alignSelf: "center", cursor: "pointer" }}
                              onClick={() => setEditAccount(item, index)}
                            />
                            <Icon
                              name="trash"
                              style={{ alignSelf: "center", cursor: "pointer" }}
                              onClick={() => {
                                setIndexAccount(index);
                                setDeleteModalOpen(true);
                                setAddAccount(false);
                                resetForm();
                              }}
                            />
                          </Label>
                        </TableCollum>
                      </TableRow>
                    </>
                  ))
                }
              /> : ''}
            </TableBody>
          </Table>


          {!addAccount && <Row>
            <Col lg={6} md={6} sm={6} xs={12}></Col>
            <Col lg={6} md={6} sm={6} xs={12}>
              <ViewText style={{ marginTop: '15px' }}>
                <Icon name="plus" onClick={() => setAddAccount(true)} style={{ cursor: 'pointer' }} />
              </ViewText>
            </Col>
          </Row>}


          {addAccount &&
            <Form>
              <div style={{ marginTop: 40, marginBottom: 10 }}>
                <Row>
                  <Col xs={12} md={3}>
                    <Field
                      name="product"
                      label="Product"
                      isRequired
                      options={getFiledOptions(products, 'product')}
                      onChange={(e) => { 
                        setFieldValue('product', e);
                        handleProductCurrencies(e);
                      }}
                      component={SelectField}
                      disabled={!hasPermission}
                    />
                  </Col>
                  {values.product || values.accountCurrency ?
                    <Col xs={12} md={3}>
                      <Field
                        name="accountCurrency"
                        label="Currency"
                        isRequired
                        options={productCurrencies}
                        onChange={(e) => { setFieldValue('accountCurrency', e)}}
                        component={SelectField}
                        disabled={!hasPermission}
                      />
                    </Col>
                  : ''}
                </Row>
                <Row>
                  {!data.length &&
                    <Col xs={12} md={3}>
                      <Field
                        name="bankName"
                        label="Bank"
                        component={SearchField}
                        searchList={searchList}
                        isRequired
                        value={values.bankName && values.bankName}
                        onChange={debouncedChangeHandler}
                        onFocus={() => {
                          setSearchList([]);
                          searchBanks("");
                        }}
                        canGenerate={false}
                        onSearchSelect={(selectedValue) => {
                          setFieldValue(`bankName`, selectedValue.lastName);
                          setFieldValue(`idPersonTailBank`, selectedValue.idPerson);
                          setSearchList([]);
                        }}
                      />
                    </Col>
                  }
                  <Col xs={12} md={3}>
                    <Field
                      name="accountName"
                      label="Name"
                      component={InputField}
                      isRequired
                      disabled={!hasPermission}
                    />
                  </Col>
                  <Col xs={12} md={3}>
                    <Field
                      name="bankSortCode"
                      label="Sort Code"
                      component={InputField}
                      isRequired
                      disabled={!hasPermission}
                    />
                  </Col>
                  <Col xs={12} md={3}>
                    <Field
                      name="bankSwiftBIC"
                      label="Swift"
                      isRequired
                      component={InputField}
                      disabled={!hasPermission}
                    />
                  </Col>
                </Row>

                <Row>
                  <Col xs={12} md={3}>
                    <Field
                      name="accountNumber"
                      label="Acc. Number"
                      isRequired
                      component={InputField}
                      disabled={!hasPermission}
                    />
                  </Col>
                  <Col xs={12} md={3}>
                    <Field
                      name="bankIBAN"
                      label="IBAN"
                      isRequired
                      component={InputField}
                      disabled={!hasPermission}
                    />
                  </Col>
                  <Col xs={12} md={3}>
                    <Field
                      name="correspbank"
                      label="Corresp. Bank"
                      isRequired
                      component={InputField}
                      disabled={!hasPermission}
                    />
                  </Col>
                  <Col xs={12} md={3}>
                    <Field
                      name="routingNumber"
                      label="ACH Routing Number"
                      component={InputField}
                      disabled={!hasPermission}
                    />
                  </Col>
                  <Col xs={12} md={3}>
                    <Field
                      name="transitAbaNumber"
                      label="Transit/ABA Number (NY)"
                      component={InputField}
                      disabled={!hasPermission}
                    />
                  </Col>
                  <Col xs={12} md={3}>
                    <Field
                      name="correspbank_BIC"
                      label="Corresp. BIC"
                      isRequired
                      component={InputField}
                      disabled={!hasPermission}
                    />
                  </Col>
                </Row>

                <ButtonContainer>
                  <ButtonContainer style={{ justifyContent: 'flex-start', alignItems: 'center', paddingBottom: '10px' }} />
                  <ButtonContent>
                    <Button
                      fullWidth
                      style={{ marginLeft: 15 }}
                      third
                      onClick={() => {
                        setIndexAccount(null);
                        setAddAccount(false);
                        resetForm();
                      }}
                    >
                      CANCEL
                    </Button>
                  </ButtonContent>
                  <ButtonContent>
                    <Button
                      fullWidth
                      style={{ marginLeft: 15 }}
                      type="submit"
                      primary
                      onClick={() => {
                        createValidation().validate(values).catch((e) => {
                          console.log('validation error', e)
                          mc.error('Please fill in all required fields.')
                        })
                      }}
                      id="btnSubmit">
                      {indexAccount !== null && indexAccount !== undefined ? "SAVE ACCOUNT" : "ADD ACCOUNT"}
                    </Button>
                  </ButtonContent>
                </ButtonContainer>
              </div>
            </Form>}
        </>
      )}
    </Formik>
  )
}

export default AccountInformation;